import React from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { reportLangToI18n, REPORT_LANGUAGE } from '../../i18n';

import useGlobalStatus from '../../hook/useGlobalStatus';
import useUtils from '../../hook/useUtils';

import { MEDIA_DATA, FACEBOOK, GOOGLE, LINE, TABOOLA } from '../../constants/mediaData';
import * as basicConstants from '../../constants/basic';

import { generateUniqueKey } from '../../utils/date';
import { excelSheets as fb_excelSheets } from '../../utils/FbFieldsDefined';
import { excelSheets as google_excelSheets } from '../../utils/GoogleFieldsDefined';
import { excelSheets as line_excelSheets } from '../../utils/LineFieldsDefined';
import { excelSheets as taboola_excelSheets } from '../../utils/TaboolaFieldsDefined';
import { origiTargetName } from '../../utils/LineFieldsDefined';

import { checkAddressesIsNull } from '../formData/formData';
import { HandleSendDate, SEND_DETAIL } from './data/sendDetailConstants';
import ModalHeader from '../pop/ModalHeader';
import ModalFooter from '../pop/ModalFooter';

import { BoxFlexStartStart } from '../customComponent/box';
import { ButtonGrey } from '../../components/customComponent/button';
import theme from '../../theme';

import {
  CorporateFareIcon,
  AccountTreeOutlinedIcon,
  DateRangeOutlinedIcon,
  GroupsOutlinedIcon,
  MailOutlineIcon,
  DescriptionOutlinedIcon,
  PagesOutlinedIcon,
  FileDownloadOutlinedIcon,
  CrisisAlertIcon,
  TextSnippetIcon
} from '../../constants/images';


const mediaSheets = {
  [FACEBOOK]: fb_excelSheets,
  [GOOGLE]: google_excelSheets,
  [LINE]: line_excelSheets,
  [TABOOLA]: taboola_excelSheets,
}

const excludeSheets = ["RAW_DATA", "Menu", "LINE_CREATIVE_VDO"];
const excludeFields = ["行銷活動", "廣告組合", "廣告名稱", "Campaign Name", "Ad Set Name", "Ad Name", "キャンペーン名", "広告セット名", "広告名"];


const SendDetail = (props) => {
  const history = useHistory();
  const { media, currentData, showDataDetail, setShowDataDetail, isProcessing, handleDownloadOrSend, handleSendAllEmails } = props;
  const { locale, setLocale, entryPath, setEntryPath } = useGlobalStatus();
  const [transformExpressToTw] = useUtils();

  const currentSheets = currentData.excelSheets.filter((item) => !excludeSheets.includes(item) && !item.includes('Chart') && !item.includes('CHART'));

  const currentCampaignGoals = media === LINE && currentData.campaignList.map((el) => {
    const goalIndex = _.findIndex(origiTargetName, c => c.campaignGoalEng === el);
    return origiTargetName[goalIndex].campaignGoalName;
  })

  const handleCurrentFields = () => {
    let fields = [];

    const processFields = (sourceFields, excelSheets) => {
      return sourceFields
        .filter((item) => item.sheet?.length > 1 || item.sheet !== null)
        .map((el) => ({
          name: el.name,
          expression: el.expression,
          type: el.type,
          sheet: _.intersection(el.sheet, excelSheets),
          indexNumber: el.indexNumber,
        }))
        .filter((item) => item.sheet.length !== 0);
    };

    switch (media) {
      case FACEBOOK:
        fields = currentData.fields.filter((item) => !excludeFields.includes(item.name));
        break;
      case GOOGLE:
        fields = processFields(currentData.fields, currentData.excelSheets);
        break;
      case LINE:
        fields = processFields(currentData.fields, currentData.campaignList);
        break;
      default:
        fields = currentData.fields;
        break;
    }

    return fields.sort((a, b) => {
      if (a.indexNumber === null) return 1;
      if (b.indexNumber === null) return -1;
      return a.indexNumber - b.indexNumber;
    });
  }

  const currentFields = handleCurrentFields();

  const handleTitle = (field) => {
    const transformExpression = transformExpressToTw(field.expression, currentData.fields);
    const format = field.format === 'PERCENT' ? '百分比' : field.format === 'DEFAULT' ? '數字' : '$';
    return `${transformExpression} (${format})`;
  }

  const handleClose = () => {
    setShowDataDetail(false);
  }

  const handleOpen = () => {
    setShowDataDetail(true);
  }

  const handleSheetName = (sheet, language) => {
    let sheetName;
    if (language.includes('en')) {
      sheetName = sheet.name_eng;
    } else {
      sheetName = sheet.name;
    }
    return sheetName;
  }

  return (
    <>
      {currentData &&
        <SwipeableDrawer
          anchor="right"
          open={showDataDetail}
          onClose={handleClose}
          onOpen={handleOpen}
          sx={{ '& .MuiPaper-root': { width: '50%' } }}
        >
          <ModalHeader
            title={<FormattedMessage id="sendList.detail.header" />}
            type="fullHeight"
            handleClose={handleClose}
          />
          <Box sx={{ overflow: 'auto', p: '32px 40px' }} >

            <BoxFlexStartStart sx={{ mb: '32px' }}>
              <CorporateFareIcon variant="grey_mt2" />
              <Typography>{currentData.weeklyReportName}</Typography>
            </BoxFlexStartStart>

            <BoxFlexStartStart sx={{ mb: '32px' }} >
              <AccountTreeOutlinedIcon variant="grey_mt2" />
              <List disablePadding dense>
                {currentData.facebookAccounts.map(el => {
                  return (
                    <ListItem key={el.adAccountId} disablePadding>
                      <ListItemText
                        primary={
                          <Typography>
                            {`${el.adAccountId} ${el.adAccountName || ''}`}
                          </Typography>
                        }
                        sx={{ margin: 0, fontSize: '1rem' }}
                      />
                    </ListItem>
                  )
                })}
              </List>
            </BoxFlexStartStart>

            <BoxFlexStartStart sx={{ mb: '32px' }} >
              <DateRangeOutlinedIcon variant="grey_mt2" />

              <Typography sx={{ color: `${theme.palette.grey.w400}` }}>
                {checkAddressesIsNull(currentData.toAddresses) ? <FormattedMessage id="sendList.detail.unsetTime" /> : null}
              </Typography>

              <Typography>
                {checkAddressesIsNull(currentData.toAddresses) ? null : HandleSendDate(currentData, 'detail')}
              </Typography>
            </BoxFlexStartStart>

            <BoxFlexStartStart sx={{ mb: '32px' }} >
              <GroupsOutlinedIcon variant="grey_mt2" />
              {checkAddressesIsNull(currentData.toAddresses) ?
                (<Typography sx={{ color: `${theme.palette.grey.w400}` }}><FormattedMessage id="sendList.detail.unsetEmail" /></Typography>
                ) : (
                  <List disablePadding dense>
                    {currentData.toAddresses.map(el => {
                      return (
                        <ListItem key={el} disablePadding>
                          <ListItemText
                            primary={<Typography>{el}</Typography>}
                            sx={{ m: 0 }}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                )
              }
            </BoxFlexStartStart>

            {SEND_DETAIL[media].sendDetail.includes('mailMessageType') &&
              <BoxFlexStartStart sx={{ mb: '32px' }}>
                <MailOutlineIcon variant="grey_mt2" />
                <Box>
                  <Typography>
                    {
                      (media === 'GOOGLE' && !currentData.campaignList.includes('all') && currentData.mailMessageExtra && Object.keys(currentData.mailMessageExtra).length > 0)
                        || media === 'FACEBOOK'
                        ? <FormattedMessage id={SEND_DETAIL[media].emailTemplate[currentData.mailMessageType]?.label} />
                        : <FormattedMessage id="emailData.template.defaultF.field" />
                    }
                  </Typography>
                  {(media === 'GOOGLE' && !currentData.campaignList.includes('all')) || media === 'FACEBOOK' ? (
                    SEND_DETAIL[media].emailTemplate[currentData.mailMessageType]?.fields
                      .filter(fields => currentData.mailMessageExtra[fields]?.length > 0)
                      .map(el => {
                        return (
                          <Box key={el}>
                            <Typography sx={{ my: 1 }}><FormattedMessage id={SEND_DETAIL[media].emailBlocks[el]} />：
                              {(currentData.mailMessageExtra[el] && currentData.mailMessageExtra[el].length > 0) &&
                                currentData.mailMessageExtra[el].map((item, index) => {
                                  const comma = index !== currentData.mailMessageExtra[el].length - 1 ? '、' : '';
                                  return (
                                    <Typography
                                      component="span"
                                      key={item.name}
                                    >
                                      {item.name}{comma}
                                    </Typography>
                                  )
                                })
                              }
                              {!currentData.mailMessageExtra[el] && <FormattedMessage id="sendList.detail.unsetField" />}
                              {(currentData.mailMessageExtra[el] && !currentData.mailMessageExtra[el].length) && <FormattedMessage id="sendList.detail.unsetField" />}
                            </Typography>
                          </Box>
                        )
                      })
                  ) : ""
                  }
                </Box>
              </BoxFlexStartStart>
            }

            {SEND_DETAIL[media].sendDetail.includes('language') &&
              <BoxFlexStartStart sx={{ mb: '32px' }} >
                <TextSnippetIcon variant="grey_mt2" />
                <Typography>
                  <FormattedMessage id={`common.language.${reportLangToI18n[currentData.language || REPORT_LANGUAGE.zh]}`} />
                  {SEND_DETAIL[media].sendDetail.includes('conversionsType') && (
                    currentData.conversionsType === "allConversions" ? <FormattedMessage id="sendList.detail.allConversion" />
                      : currentData.conversionsType === "conversions" ? <FormattedMessage id="sendList.detail.conversion" />
                        : <FormattedMessage id="sendList.detail.noConversion" />)}
                </Typography>
              </BoxFlexStartStart>
            }

            {SEND_DETAIL[media].sendDetail.includes('campaignGoals') &&
              <BoxFlexStartStart sx={{ mb: '32px' }} >
                <CrisisAlertIcon variant="grey_mt2" />
                <Box>
                  {currentCampaignGoals.map((el, index) => {
                    const comma = index !== currentCampaignGoals.length - 1 ? '、' : ''
                    return (
                      <Typography component="span" key={`${el}_${index}`}>
                        {el}{comma}
                      </Typography>
                    )
                  })}
                </Box>
              </BoxFlexStartStart>
            }

            <BoxFlexStartStart sx={{ mb: '32px' }} >
              <DescriptionOutlinedIcon variant="grey_mt2" />
              <Box>
                {currentSheets.map((el, index) => {
                  const currentPage = mediaSheets[media].filter(page => page.original_name === el)[0];
                  const comma = index !== currentSheets.length - 1 ? '、' : '';

                  return (
                    <Typography component="span" key={`${el}-${generateUniqueKey}`}>
                      <FormattedMessage id={currentPage.name} />{comma}
                    </Typography>
                  )
                })}
              </Box>
            </BoxFlexStartStart>

            <BoxFlexStartStart sx={{ mb: '32px' }} >
              <PagesOutlinedIcon variant="grey_mt2" />
              <Box>
                {currentFields.map((el, index) => {
                  const comma = index !== currentFields.length - 1 ? '、' : ''
                  const title = el.type === 'CUSTOM' ? handleTitle(el) : null;
                  return (
                    <Typography
                      component="span"
                      key={el.name}
                      title={title}
                    >
                      {el.name}{comma}
                    </Typography>
                  )
                })}
              </Box>
            </BoxFlexStartStart>

            {currentData.status === 'ACTIVE' &&
              <BoxFlexStartStart>
                <FileDownloadOutlinedIcon variant="dark" sx={{ mr: '8px' }} />
                <ButtonGrey
                  name="testDownload"
                  size="small"
                  sx={{ mr: '8px' }}
                  endIcon={isProcessing === 'testDownload' ? <CircularProgress size={16} color="inherit" /> : null}
                  disabled={isProcessing === 'testDownload'}
                  onClick={handleDownloadOrSend}
                >
                  <FormattedMessage id="sendList.detail.button.testDownload" />
                </ButtonGrey>

                <ButtonGrey
                  name="testSend"
                  size="small"
                  sx={{ mr: '8px' }}
                  endIcon={isProcessing === 'testSend' ? <CircularProgress size={16} color="inherit" /> : null}
                  disabled={isProcessing === 'testSend'}
                  onClick={handleDownloadOrSend}
                >
                  <FormattedMessage id="sendList.detail.button.testSend" />
                </ButtonGrey>

                <ButtonGrey
                  name="sendAllEmails"
                  size="small"
                  endIcon={isProcessing === 'sendAllEmails' ? <CircularProgress size={16} color="inherit" /> : null}
                  disabled={isProcessing === 'sendAllEmails'}
                  onClick={handleSendAllEmails}
                >
                  <FormattedMessage id="sendList.detail.button.sendAllEmails" />
                </ButtonGrey>
              </BoxFlexStartStart>
            }
          </Box>

          <Box sx={{ mt: 'auto', pb: '16px' }}>
            <ModalFooter
              checkBottonContent={<FormattedMessage id="common.button.edit" />}
              cancelBottonContent={<FormattedMessage id="common.button.close" />}
              handelCancel={handleClose}
              handleSubmit={() => {
                setEntryPath(MEDIA_DATA[media].editDataUrl);
                history.push(MEDIA_DATA[media].createDataUrl);
              }}
            />
          </Box>
        </SwipeableDrawer>
      }
    </>
  )
}

export default SendDetail;